import { CARRIER_NAME, ClaimOutcome, ClaimStatus, OUTCOME_LABELS, STATUS_ORDER } from './claimsData'

export enum StepState {
  DONE = 'DONE',
  CURRENT = 'CURRENT',
  PENDING = 'PENDING',
}

/** A finished step is not always a win — Denied completes the claim too. */
export enum StepTone {
  SUCCESS = 'SUCCESS',
  DANGER = 'DANGER',
  NEUTRAL = 'NEUTRAL',
}

export enum StepDetail {
  ADJUSTER = 'ADJUSTER',
  NEEDS_INPUT = 'NEEDS_INPUT',
  OUTCOME = 'OUTCOME',
}

export interface TimelineStep {
  key: string
  title: string
  when?: string
  desc?: string
  state: StepState
  tone?: StepTone
  /** Cards rendered inside the step — things that happen during it, not stages of their own. */
  details?: StepDetail[]
}

const { DONE, CURRENT, PENDING } = StepState

const OUTCOME_TONES: Record<ClaimOutcome, StepTone> = {
  [ClaimOutcome.PAID]: StepTone.SUCCESS,
  [ClaimOutcome.DENIED]: StepTone.DANGER,
  [ClaimOutcome.CLOSED_NO_PAYMENT]: StepTone.NEUTRAL,
  [ClaimOutcome.WITHDRAWN]: StepTone.NEUTRAL,
}

function stateFor(step: ClaimStatus, current: ClaimStatus): StepState {
  const a = STATUS_ORDER.indexOf(step)
  const b = STATUS_ORDER.indexOf(current)
  if (a < b) return DONE
  if (a === b) return CURRENT
  return PENDING
}

/**
 * One flat run of steps. Who is holding the claim is carried by each step's own
 * wording rather than by splitting the timeline into Cai and carrier sections.
 */
export function timelineForStatus(
  status: ClaimStatus,
  outcome?: ClaimOutcome,
  needsYou?: boolean
): TimelineStep[] {
  const submitted = status !== ClaimStatus.DRAFT
  const filed = stateFor(ClaimStatus.FILED, status)
  const open = stateFor(ClaimStatus.OPEN_WITH_CARRIER, status)
  const atCarrier = open !== PENDING
  const closed = status === ClaimStatus.CLOSED

  const openDetails: StepDetail[] = atCarrier ? [StepDetail.ADJUSTER] : []
  if (needsYou) openDetails.push(StepDetail.NEEDS_INPUT)

  return [
    // An event backed by `Claim.submitted_at`; the customer's action finishes on signature.
    {
      key: 'submitted',
      title: 'Submitted',
      when: submitted ? 'Aug 29, 9:42 AM' : undefined,
      desc: submitted ? 'Reported and signed by Ted Johnson' : 'Lands here the moment you submit the claim.',
      state: submitted ? DONE : PENDING,
    },
    {
      key: 'in-review',
      title: 'In review with Cai',
      when: submitted ? 'Aug 29, 11:20 AM' : undefined,
      desc: 'Your broker is checking coverage under policy 877053097.',
      state: stateFor(ClaimStatus.IN_REVIEW, status),
    },
    {
      key: 'filed',
      title: `Filed to ${CARRIER_NAME}`,
      when: filed === PENDING ? undefined : 'Aug 30, 10:05 AM',
      desc:
        filed === PENDING
          ? 'We file this as soon as your broker signs off.'
          : 'Sent as a first notice of loss under policy ILM0721336',
      state: filed,
    },
    {
      key: 'open',
      title: `Open in process with ${CARRIER_NAME}`,
      when: atCarrier ? (closed ? 'Sep 1 to Sep 12' : 'Last update Sep 4') : undefined,
      desc: atCarrier
        ? needsYou
          ? 'Adjuster assigned Sep 1. Jane asked for two more photos on Sep 3, and the claim waits on you until they arrive.'
          : 'Adjuster assigned Sep 1. The carrier tells us only whether a claim is open or closed, so call the adjuster for anything finer.'
        : 'Starts once the carrier takes the claim.',
      state: closed ? DONE : open,
      details: openDetails,
    },
    {
      key: 'closed',
      title: closed && outcome ? `Closed · ${OUTCOME_LABELS[outcome]}` : 'Closed',
      when: closed ? 'Sep 12' : undefined,
      desc: closed ? undefined : 'The carrier closes the claim once it settles, is denied, or is withdrawn.',
      state: closed ? DONE : PENDING,
      tone: closed && outcome ? OUTCOME_TONES[outcome] : undefined,
      details: closed ? [StepDetail.OUTCOME] : undefined,
    },
  ]
}
